/**
 * Transcription detail — transcript, request metadata, and the full event timeline used to
 * troubleshoot a job a third-party integrator reported by correlation id.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy as CopyIcon, ExternalLink as OpenIcon, RefreshCw as RefreshIcon } from 'lucide-react';
import { Button, Chip, ShellHeader, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { transcriptionService } from '../services/transcriptionService';
import type { TranscriptionTransactionDto } from '../types/transcription';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

/* STATUS_COLORS — kept as reference for Chip mapping after migration. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const STATUS_COLORS: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  Completed: 'success',
  Failed: 'error',
  Processing: 'info',
  Pending: 'warning',
  Cancelled: 'default',
};
void STATUS_COLORS;

const LEVEL_COLORS: Record<string, 'green' | 'red' | 'amber' | 'default'> = {
  Info: 'default',
  Warn: 'amber',
  Error: 'red',
};

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="min-w-[180px]">
    <span className="text-xs text-[var(--color-text-secondary)] block">
      {label}
    </span>
    <p className="text-sm break-all">
      {value === null || value === undefined || value === '' ? '—' : value}
    </p>
  </div>
);

export const TranscriptionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [transaction, setTransaction] = useState<TranscriptionTransactionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setTransaction(await transcriptionService.getTransaction(id));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load transcription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    addNotification(`${label} copied to clipboard`, 'success');
  };

  if (loading) {
    return (
      <div className="transcription-detail" data-testid="transcription-detail-page">
        <ShellHeader title="Transcription" description="Loading…" />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid="transcription-detail-loading" />
        </PageContainer>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="transcription-detail" data-testid="transcription-detail-page">
        <ShellHeader
          title="Transcription"
          description="Transaction not found."
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.TRANSCRIPTIONS)} data-testid="back-transcription-btn">
              Back to transactions
            </Button>
          }
        />
        <PageContainer>
          <InlineErrorPanel message={error ?? 'Transcription not found.'} data-testid="transcription-detail-error" />
        </PageContainer>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="transcription-detail" data-testid="transcription-detail-page">
      <ShellHeader
        title="Transcription"
        description={`${transaction.status} · ${transaction.mode}${transaction.agentSlug ? ` · Agent: ${transaction.agentSlug}` : ''}`}
        actions={
          <div className="flex items-center gap-1">
            {transaction.correctionSessionId && !transaction.correctedText && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => navigate(ROUTES.SESSION_DETAIL.replace(':id', transaction.correctionSessionId!))}
                data-testid="transcription-link-session"
              >
                Open session
                <OpenIcon className="h-4 w-4" />
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={load} aria-label="Refresh" data-testid="refresh-transcription-btn">
              <RefreshIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.TRANSCRIPTIONS)} data-testid="back-transcription-btn">
              Back
            </Button>
          </div>
        }
      />

      <PageContainer>
      <FormSection title="Transaction Details" data-testid="transcription-section-details">
        <div className="flex gap-4 flex-wrap">
          <div className="min-w-[320px]">
            <span className="text-xs text-[var(--color-text-secondary)] block">
              Correlation id
            </span>
            <div className="flex items-center gap-0.5">
              <p className="text-sm font-mono">
                {transaction.correlationId}
              </p>
              <button type="button" className="p-1 rounded hover:bg-[var(--color-bg-subtle)]" onClick={() => copy(transaction.correlationId, 'Correlation id')}>
                <CopyIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <Field label="Your correlation id" value={transaction.clientCorrelationId} />
          <Field label="Transaction id" value={transaction.id} />
          <Field label="Agent" value={transaction.agentSlug} />
          <Field label="Language" value={transaction.language} />
          <Field label="Provider" value={transaction.provider} />
          <Field label="File" value={transaction.fileName} />
          <Field
            label="Audio size"
            value={transaction.audioSizeBytes ? `${Math.round(transaction.audioSizeBytes / 1024)} KB` : null}
          />
          <Field
            label="Duration"
            value={transaction.durationSeconds ? `${transaction.durationSeconds.toFixed(1)}s` : null}
          />
          <Field label="Created" value={new Date(transaction.createdAt).toLocaleString()} />
          <Field
            label="Completed"
            value={transaction.completedAt ? new Date(transaction.completedAt).toLocaleString() : null}
          />
          <Field label="Request id" value={transaction.httpRequestId} />
        </div>

        {transaction.status === 'Failed' && (
          <InlineErrorPanel
            message={`${transaction.errorCode} — ${transaction.errorMessage}`}
          />
        )}
      </FormSection>

      <FormSection title="Transcript" data-testid="transcription-section-transcript">
        <div className="flex items-center justify-between mb-1">
          <p className="text-base font-medium">Transcript</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-1 rounded hover:bg-[var(--color-bg-subtle)] disabled:opacity-50"
                disabled={!transaction.text}
                onClick={() => copy(transaction.text ?? '', 'Transcript')}
              >
                <CopyIcon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Copy transcript</TooltipContent>
          </Tooltip>
        </div>
        <div
          className="p-2 max-h-[320px] overflow-auto border border-[var(--color-border-default)] rounded bg-[var(--color-bg-subtle)]"
        >
          {transaction.text ? (
            <pre className="text-sm whitespace-pre-wrap m-0">
              {transaction.text}
            </pre>
          ) : (
            <span className="text-xs text-[var(--color-text-secondary)]">
              No transcript yet.
            </span>
          )}
        </div>
      </FormSection>

      {transaction.correctedText && (
        <FormSection title={`Corrected transcript${transaction.agentSlug ? ` (by ${transaction.agentSlug})` : ''}`}>
          <div className="flex items-center justify-end gap-1 mb-1">
            {transaction.correctionSessionId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() =>
                  navigate(ROUTES.SESSION_DETAIL.replace(':id', transaction.correctionSessionId!))
                }
              >
                Open session
                <OpenIcon className="h-4 w-4" />
              </Button>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-[var(--color-bg-subtle)]"
                  onClick={() => copy(transaction.correctedText ?? '', 'Corrected transcript')}
                >
                  <CopyIcon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Copy corrected transcript</TooltipContent>
            </Tooltip>
          </div>
          <div
            className="p-2 max-h-[320px] overflow-auto border border-[var(--color-border-default)] rounded bg-[var(--color-bg-subtle)]"
          >
            <pre className="text-sm whitespace-pre-wrap m-0">
              {transaction.correctedText}
            </pre>
          </div>
        </FormSection>
      )}

      <FormSection title="Event log" data-testid="transcription-section-events">
        {transaction.events.length === 0 ? (
          <span className="text-xs text-[var(--color-text-secondary)]">
            No events recorded.
          </span>
        ) : (
          transaction.events.map((event, index) => (
            <div key={event.id}>
              {index > 0 && <hr className="border-[var(--color-border-default)] my-1.5" />}
              <div className="flex gap-2 items-start">
                <span className="text-xs text-[var(--color-text-secondary)] min-w-[170px]">
                  {new Date(event.createdAt).toLocaleString()}
                </span>
                <Chip label={event.level} className="text-xs" color={LEVEL_COLORS[event.level] ?? 'default'} />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {event.eventType}
                  </p>
                  {event.message && (
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      {event.message}
                    </p>
                  )}
                  {event.dataJson && (
                    <pre
                      className="text-xs font-mono whitespace-pre-wrap mt-0.5 text-[var(--color-text-secondary)]"
                    >
                      {event.dataJson}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </FormSection>
      </PageContainer>
    </div>
    </TooltipProvider>
  );
};
